import React, { useState, useEffect } from 'react';

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  const [confirmData, setConfirmData] = useState(null);

  useEffect(() => {
    // Interceptar window.alert
    const originalAlert = window.alert;
    
    const customAlert = (message) => {
      // Determinar el tipo de toast basado en palabras clave del mensaje
      let type = 'info';
      const msgUpper = String(message).toUpperCase();
      
      const isSuccess = [
        'ÉXITO', 'EXITO', 'GUARDADO', 'CREADO', 'COMPLETADO', 'EXITOSAMENTE', 
        'GRACIAS', 'CON ÉXITO', 'CON EXITO', 'CORRECTO', 'CORRECTAMENTE', '✅', '✨', '🎉'
      ].some(keyword => msgUpper.includes(keyword));

      const isError = [
        'ERROR', 'FALLÓ', 'FALLO', 'INCORRECTO', 'INVÁLIDO', 'INVALIDO', 
        'INCOMPLETO', 'NO SE PUDO', 'INCORRECTA', '❌', 'ERROR:', 'FALLA'
      ].some(keyword => msgUpper.includes(keyword));

      const isWarning = [
        'ADVERTENCIA', '⚠️', 'CUIDADO', 'ATENCIÓN', 'ATENCION', 'ALERTA'
      ].some(keyword => msgUpper.includes(keyword));

      if (isError) type = 'error';
      else if (isWarning) type = 'warning';
      else if (isSuccess) type = 'success';

      showToast(message, type);
    };

    window.alert = customAlert;

    // Proveer una función global window.showToast para uso directo
    const showToast = (message, type = 'info', duration = 4000) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, message, type, duration }]);
      
      setTimeout(() => {
        removeToast(id);
      }, duration);
    };

    window.showToast = showToast;

    // Proveer una función global window.confirmAsync que devuelve una promesa
    window.confirmAsync = (message) => {
      return new Promise((resolve) => {
        setConfirmData({ message, resolve });
      });
    };

    const handleToastEvent = (e) => {
      const { message, type, duration } = e.detail || {};
      showToast(message, type, duration);
    };

    window.addEventListener('show-toast', handleToastEvent);

    return () => {
      window.alert = originalAlert;
      delete window.showToast;
      delete window.confirmAsync;
      window.removeEventListener('show-toast', handleToastEvent);
    };
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  return (
    <>
      <style>{`
        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 99999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 380px;
          width: 90%;
          pointer-events: none;
        }

        .toast-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px 20px;
          border-radius: 14px;
          color: #ffffff;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-size: 0.9rem;
          line-height: 1.4;
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          pointer-events: auto;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          animation: toastSlideIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          transition: all 0.3s ease;
        }

        .toast-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.4);
        }

        /* Variantes de Color HSL Elegantes */
        .toast-item.toast-success {
          background: rgba(16, 185, 129, 0.15);
          border-color: rgba(16, 185, 129, 0.3);
          box-shadow: 0 8px 32px 0 rgba(16, 185, 129, 0.15);
        }
        .toast-item.toast-success::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #10b981;
          width: 100%;
          animation: toastProgress var(--duration, 4000ms) linear forwards;
        }

        .toast-item.toast-error {
          background: rgba(239, 68, 68, 0.15);
          border-color: rgba(239, 68, 68, 0.3);
          box-shadow: 0 8px 32px 0 rgba(239, 68, 68, 0.15);
        }
        .toast-item.toast-error::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #ef4444;
          width: 100%;
          animation: toastProgress var(--duration, 4000ms) linear forwards;
        }

        .toast-item.toast-warning {
          background: rgba(245, 158, 11, 0.15);
          border-color: rgba(245, 158, 11, 0.3);
          box-shadow: 0 8px 32px 0 rgba(245, 158, 11, 0.15);
        }
        .toast-item.toast-warning::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #f59e0b;
          width: 100%;
          animation: toastProgress var(--duration, 4000ms) linear forwards;
        }

        .toast-item.toast-info {
          background: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 8px 32px 0 rgba(59, 130, 246, 0.15);
        }
        .toast-item.toast-info::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #3b82f6;
          width: 100%;
          animation: toastProgress var(--duration, 4000ms) linear forwards;
        }

        .toast-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
          user-select: none;
        }

        .toast-content {
          flex-grow: 1;
          font-weight: 500;
        }

        .toast-close {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          font-size: 1.1rem;
          padding: 0 0 0 8px;
          line-height: 1;
          transition: color 0.2s;
          flex-shrink: 0;
        }

        .toast-close:hover {
          color: #ffffff;
        }

        /* Diálogo de Confirmación Estético */
        .confirm-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(10, 14, 23, 0.8);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 100000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.25s ease-out;
        }

        .confirm-card {
          background: rgba(30, 41, 59, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
          border-radius: 20px;
          width: 100%;
          max-width: 440px;
          padding: 28px;
          text-align: center;
          color: white;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          animation: scaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .confirm-title-area {
          font-size: 2.2rem;
          margin-bottom: 12px;
        }

        .confirm-card h3 {
          margin: 0 0 12px 0;
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .confirm-card p {
          margin: 0 0 24px 0;
          font-size: 0.95rem;
          color: #94a3b8;
          line-height: 1.5;
        }

        .confirm-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .confirm-btn {
          flex: 1;
          padding: 12px 24px;
          border-radius: 12px;
          border: none;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .confirm-btn.cancel {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          color: #e2e8f0;
        }

        .confirm-btn.cancel:hover {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .confirm-btn.confirm {
          background: linear-gradient(135deg, #ef4444, #b91c1c);
          color: white;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
        }

        .confirm-btn.confirm:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(239, 68, 68, 0.3);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleUp {
          from { transform: scale(0.9) translateY(10px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }

        @keyframes toastSlideIn {
          from {
            transform: translateX(120%) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes toastProgress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        @media (max-width: 480px) {
          .toast-container {
            top: auto;
            bottom: 20px;
            right: 5%;
            left: 5%;
            width: 90%;
            max-width: none;
          }
          @keyframes toastSlideIn {
            from {
              transform: translateY(120%) scale(0.9);
              opacity: 0;
            }
            to {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
          }
        }
      `}</style>

      <div className="toast-container">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast-item toast-${toast.type}`}
            style={{ '--duration': `${toast.duration}ms` }}
            onClick={() => removeToast(toast.id)}
          >
            <span className="toast-icon">{getIcon(toast.type)}</span>
            <div className="toast-content">{toast.message}</div>
            <button className="toast-close" onClick={(e) => {
              e.stopPropagation();
              removeToast(toast.id);
            }}>×</button>
          </div>
        ))}
      </div>

      {confirmData && (
        <div className="confirm-backdrop" onClick={() => {
          confirmData.resolve(false);
          setConfirmData(null);
        }}>
          <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-title-area">🛡️</div>
            <h3>Confirmar Acción</h3>
            <p>{confirmData.message}</p>
            <div className="confirm-buttons">
              <button className="confirm-btn cancel" onClick={() => {
                confirmData.resolve(false);
                setConfirmData(null);
              }}>
                Cancelar
              </button>
              <button className="confirm-btn confirm" onClick={() => {
                confirmData.resolve(true);
                setConfirmData(null);
              }}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ToastContainer;
